import type { User } from "@assetwatch/auth";
import { z } from "zod";
import { postMlApi } from "../../api/mlApi";
import config from "../../config";
import {
  assertCanAccessMlFrontend,
  type MlFrontendAccessFlags,
} from "../../lib/access";

/**
 * The CME feedback page's ML API calls. `list_day` is one date of flex
 * slow-trend detections with MySQL names and any saved review from S3.
 * `feature_series` is one feature's history. `save_feedback` writes one S3
 * review per monitoring point.
 *
 * Send these through `postMlApi`. That helper SigV4-signs for API Gateway.
 */

export const feedbackVerdictSchema = z.enum(["useful", "not_useful"]);
export type FeedbackVerdict = z.infer<typeof feedbackVerdictSchema>;

/** One rated feature, as `save_feedback` takes it and `list_day` returns it. */
const featureFeedbackSchema = z.object({
  feature_group: z.string(),
  feature: z.string(),
  verdict: feedbackVerdictSchema,
});

/** Only an https link is rendered; anything else is treated as no link. */
const httpsUrlSchema = z
  .string()
  .refine((value) => {
    try {
      return new URL(value).protocol === "https:";
    } catch {
      return false;
    }
  })
  .nullable()
  .catch(null);

/** One detection a CME is asked to review, as listed in the rail. */
export const feedbackItemSchema = z.object({
  id: z.string(),
  facility: z.string(),
  machine: z.string(),
  /** Not every monitoring point is mapped to a component. */
  component: z.string().nullable(),
  monitoring_point: z.string(),
  monitoring_point_id: z.string(),
  /** The asset the monitoring point is on, for the AssetWatch Web link. Null when unmapped. */
  asset_id: z.string().nullable(),
  /** Feature group and the one feature from it that's plotted, e.g. RpmHarmonics · a1z. */
  feature_group: z.string(),
  feature: z.string(),
  /** YYYY-MM-DD, the day the slow trend was detected. */
  detected_date: z.string(),
  /** Unix seconds where the detected trend starts, or null when the detector didn't say. */
  trend_start: z.number().int().nullable(),
  /** Unix seconds of the detection itself — the end of the trend window. */
  detected_at: z.number().int(),
  /** Null until someone has reviewed it. */
  verdict: feedbackVerdictSchema.nullable(),
  comment: z.string().nullable(),
});
export type FeedbackItem = z.infer<typeof feedbackItemSchema>;

const cmeFeedbackCostSchema = z.object({
  usd: z.number().nullish().catch(null),
  mb_scanned: z.number().nullish().catch(null),
  rows: z.number().nullish().catch(null),
  seconds: z.number().nullish().catch(null),
});
export type CmeFeedbackCost = z.infer<typeof cmeFeedbackCostSchema>;

export const listItemsResponseSchema = z.object({
  items: z.array(feedbackItemSchema),
  items_truncated: z.boolean().nullish().catch(null),
  cost: cmeFeedbackCostSchema.nullish().catch(null),
});
export type ListItemsResponse = z.infer<typeof listItemsResponseSchema>;

const CME_FEEDBACK_PATH = `${config.mlApi.frontendPath}/cme_feedback`;

/**
 * One feature's time series for one item. `values` is index-aligned with
 * `timestamps` (Unix seconds), and a null is a real gap, not a zero.
 */
export const featureSeriesResponseSchema = z.object({
  item_id: z.string(),
  feature: z.string(),
  y_units: z.string(),
  timestamps: z.array(z.number()),
  values: z.array(z.number().nullable()),
  cost: cmeFeedbackCostSchema.nullish().catch(null),
});
export type FeatureSeriesResponse = z.infer<typeof featureSeriesResponseSchema>;

export type FeatureSeriesRequest = {
  id: string;
  monitoring_point_id: string;
  feature: string;
  feature_group: string;
  end_date: string;
};

export async function fetchCmeFeatureSeries({
  user,
  flags,
  item,
  days,
}: {
  user: User | null;
  flags: MlFrontendAccessFlags;
  item: FeatureSeriesRequest;
  days: number;
}): Promise<FeatureSeriesResponse> {
  assertCanAccessMlFrontend({ flags, user });

  const payload = await postMlApi(CME_FEEDBACK_PATH, {
    meth: "feature_series",
    item_id: item.id,
    monitoring_point_id: item.monitoring_point_id,
    feature: item.feature,
    feature_group: item.feature_group,
    end: item.end_date,
    days,
  });
  return featureSeriesResponseSchema.parse(payload);
}

export const detectedFeatureSchema = z.object({
  name: z.string(),
  feature_group: z.string(),
});
export type DetectedFeature = z.infer<typeof detectedFeatureSchema>;

export const cmeAssetSchema = z.object({
  id: z.string(),
  monitoring_point_id: z.string(),
  monitoring_point: z.string(),
  machine: z.string(),
  facility: z.string(),
  customer_name: z.string(),
  cme_name: z.string(),
  url: httpsUrlSchema,
  severity: z.number().nullable(),
  severity_feature: z.string(),
  severity_feature_group: z.string(),
  /** Every feature that trended that day. The first is the highest severity. */
  features: z.array(detectedFeatureSchema).default([]),
  feature_groups: z.string(),
  feature_trend_days: z.string(),
  tooltip: z.string(),
  feature: z.string(),
  detected_date: z.string(),
  label: z.string().nullable(),
  external_customer_id: z.string().nullable(),
  machine_id: z.number().int().nullable(),
  facility_id: z.number().int().nullable(),
  /**
   * MySQL MonitoringPointID: the card AssetWatch Web scrolls to (`#id`) on the
   * asset page. Defaults to null so the page works before the API sends it.
   */
  monitoring_point_db_id: z.number().int().nullable().default(null),
  cme_user_id: z.number().int().nullable(),
  /** Filled from the saved S3 review. Null until someone has reviewed this point. */
  verdict: feedbackVerdictSchema.nullable().default(null),
  comment: z.string().nullable().default(null),
  /**
   * The saved per-feature verdicts. Re-saving without them would overwrite the
   * stored ones, so the page starts from these.
   */
  feature_verdicts: z.array(featureFeedbackSchema).default([]),
});
export type CmeAsset = z.infer<typeof cmeAssetSchema>;

export const cmeOptionSchema = z.object({
  cme_user_id: z.number().int(),
  cme_name: z.string(),
});
export type CmeOption = z.infer<typeof cmeOptionSchema>;

export const listDayResponseSchema = z.object({
  items: z.array(cmeAssetSchema),
  /** Every CME with a detection that day, collected before the id filter. */
  cmes: z.array(cmeOptionSchema).default([]),
  /** Name for the requested id, including a CME with no detections that day. */
  cme_name: z.string().nullable().default(null),
  cost: cmeFeedbackCostSchema.nullish().catch(null),
});

export type DayListResult = {
  items: CmeAsset[];
  cmes: CmeOption[];
  cme_name: string | null;
  /** The day's Athena scan. Null when the backend didn't report one. */
  cost: CmeFeedbackCost | null;
};

export async function fetchCmeDay({
  user,
  flags,
  date,
  cmeUserId,
}: {
  user: User | null;
  flags: MlFrontendAccessFlags;
  date: string;
  cmeUserId?: number;
}): Promise<DayListResult> {
  assertCanAccessMlFrontend({ flags, user });
  const payload = await postMlApi(CME_FEEDBACK_PATH, {
    meth: "list_day",
    date,
    ...(cmeUserId === undefined ? {} : { cme_user_id: cmeUserId }),
  });
  const parsed = listDayResponseSchema.parse(payload);
  return { ...parsed, cost: parsed.cost ?? null };
}

/** Show Description text, keyed by lowercase feature stem (e1, av2, fft_harm_amp_120). */
export const featureDescriptionsResponseSchema = z.object({
  descriptions: z.record(z.string(), z.string()),
});

export async function fetchCmeFeatureDescriptions({
  user,
  flags,
}: {
  user: User | null;
  flags: MlFrontendAccessFlags;
}): Promise<Record<string, string>> {
  assertCanAccessMlFrontend({ flags, user });
  const payload = await postMlApi(CME_FEEDBACK_PATH, {
    meth: "feature_descriptions",
  });
  return featureDescriptionsResponseSchema.parse(payload).descriptions;
}

/** One monitoring point on the machine being saved. */
export const saveFeedbackPointSchema = z.object({
  monitoring_point_id: z.string(),
  monitoring_point: z.string(),
  verdict: feedbackVerdictSchema.nullable(),
  comment: z.string().max(2000),
  features: z.array(featureFeedbackSchema),
});
export type SaveFeedbackPoint = z.infer<typeof saveFeedbackPointSchema>;

/** What "Save feedback" will send. One object is written per point. */
export const saveFeedbackRequestSchema = z.object({
  date: z.string(),
  cme_name: z.string(),
  machine_id: z.number().int().nullable(),
  machine: z.string(),
  points: z.array(saveFeedbackPointSchema).min(1),
});
export type SaveFeedbackRequest = z.infer<typeof saveFeedbackRequestSchema>;

/** Writes each point to S3 through the ML API. */
export async function saveCmeFeedback({
  user,
  flags,
  feedback,
}: {
  user: User | null;
  flags: MlFrontendAccessFlags;
  feedback: SaveFeedbackRequest;
}): Promise<void> {
  assertCanAccessMlFrontend({ flags, user });
  await postMlApi(CME_FEEDBACK_PATH, {
    meth: "save_feedback",
    ...feedback,
  });
}
